import { useMemo, memo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Check } from "lucide-react";

import { EquipmentSelectProps } from "../../../../../types/baseTypes";
import { convertMinutesToHours, formatCurrency, getEquipmentName } from "../../../../../util";
import { RootState } from "../../../../../redux/store";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../../../../../components/ui/accordion";
import { cn } from "lib/utils";
import { useTaxStatement } from "../../../../../util/taxStatement";

interface EquipmentListAccordionProps extends EquipmentSelectProps {
    /** Whether this accordion is expanded */
    expanded: boolean;
    /** Callback invoked when accordion expansion state changes */
    onAccordionChange: (expanded: boolean) => void;
    /** Whether this equipment is the currently selected one (drives "selected" visual feedback) */
    selected?: boolean;
}

function EquipmentListAccordion(props: EquipmentListAccordionProps) {
    const { t, i18n } = useTranslation();
    const taxStatement = useTaxStatement();
    const tenantConfig = useSelector((state: RootState) => state.tenantConfig.config);
    // Use authoritative Redux useCase instead of sessionStorage-backed service.
    // sessionStorage's selectedService is set late in the SUP flow (only on the
    // personalData -> confirmation transition), so the previous fallback to KEYS
    // produced wrong unit ("/Tag") and hid the price-detail list on the SUP page.
    const useCase = useSelector((state: RootState) => state.bookingCreation.useCase);

    const {
        onAccordionChange,
        expanded,
        equipment,
        name,
        langNames,
        image,
        selected = false,
    } = props;

    // Transform price scale data into a keyed map: duration -> prices[]
    const priceScales = useMemo(
        () =>
            equipment.priceCategoryInfo?.priceScale?.reduce(
                (acc: Record<string, number[]>, curr) => {
                    const duration = curr.durationInMinutes;
                    const price = curr?.price ?? 0;

                    if (!acc[duration]) {
                        acc[duration] = [price];
                    } else {
                        acc[duration].push(price);
                    }
                    return acc;
                },
                {}
            ),
        [equipment.priceCategoryInfo?.priceScale]
    );

    // Sort durations in ascending order for display
    const sortedDurations = useMemo(
        () => Object.keys(priceScales || {}).map(Number).sort((a, b) => a - b),
        [priceScales]
    );

    // Sort prices within each duration (ascending)
    const sortedPriceScales = useMemo(
        () =>
            sortedDurations.reduce((acc: Record<string, number[]>, duration) => {
                acc[duration] = (priceScales?.[duration] || []).slice().sort((a, b) => a - b);
                return acc;
            }, {}),
        [sortedDurations, priceScales]
    );

    const lowestMinute = sortedDurations.length > 0 ? sortedDurations[0] : null;

    const formattedPrice = useMemo(() => {
        const price = (lowestMinute !== null ? sortedPriceScales[lowestMinute]?.[0] : 0) ?? 0;
        return new Intl.NumberFormat(i18n.language, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(price);
    }, [i18n.language, lowestMinute, sortedPriceScales]);

    // Check if any duration has multiple price points (interval pricing)
    const priceIncludeInterval = useMemo(
        () => Object.values(priceScales || {}).some((arr) => arr.length > 1),
        [priceScales]
    );

    // Localized equipment title for display and accessibility
    const equipmentTitle = useMemo(
        () => getEquipmentName(name, langNames, "title") || "",
        [name, langNames]
    );

    // ShadCN Accordion uses a string value to determine open state. We map a single
    // "open" item id to expansion. When the user toggles the trigger, Radix passes
    // the new value (id or empty string) — translate that into the boolean
    // expanded callback expected by the parent.
    const handleValueChange = useCallback(
        (value: string) => {
            onAccordionChange(value === equipment.id);
        },
        [onAccordionChange, equipment.id]
    );

    return (
        <div
            className={cn(
                "relative w-full rounded-lg border transition-all text-slate-900",
                "focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2",
                selected
                    ? "border-primary bg-primary/5 shadow-md"
                    : "border-slate-200 bg-white hover:border-primary hover:shadow-md"
            )}
        >
            <Accordion
                type="single"
                collapsible
                value={expanded ? equipment.id : ""}
                onValueChange={handleValueChange}
            >
                <AccordionItem value={equipment.id} className="border-b-0">
                    <AccordionTrigger className="px-3 py-3 hover:no-underline rounded-lg focus:outline-none focus-visible:outline-none [&>svg]:hidden">
                        <div className="flex justify-start items-center w-full gap-3">
                            <div className="flex-shrink-0">
                                <img
                                    src={image}
                                    alt={equipmentTitle}
                                    className="h-16 w-16 rounded-md object-cover"
                                />
                            </div>
                            <div className="flex-1 min-w-0 text-left">
                                <p
                                    className={cn(
                                        "font-semibold tracking-[0.32px] leading-5 overflow-hidden text-ellipsis [display:-webkit-box] [-webkit-box-orient:vertical]",
                                        expanded
                                            ? "[-webkit-line-clamp:10]"
                                            : "text-base [-webkit-line-clamp:2] max-h-12"
                                    )}
                                >
                                    {equipmentTitle}
                                </p>
                                <p className={cn(!expanded && "text-base")}>
                                    {t("book_location.from")}{" "}
                                    <span className="font-bold leading-[20px] tracking-[0.02em]">
                                        <span>{formattedPrice}</span>{" "}
                                        <span>{tenantConfig.currencySymbol}</span>
                                        {equipment.priceCategory === "subscription" ? (
                                            <span> / {t("book_location.month")}</span>
                                        ) : useCase === "keys" ? (
                                            <span> / {t("book_location.day")}</span>
                                        ) : (
                                            <span>
                                                {" "}
                                                / {lowestMinute ? convertMinutesToHours(lowestMinute) : 0}
                                            </span>
                                        )}
                                    </span>
                                </p>
                            </div>
                            {selected && (
                                <div className="flex-shrink-0 bg-primary text-white rounded-full p-1 shadow-sm">
                                    <Check className="h-4 w-4" />
                                </div>
                            )}
                        </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-3 pb-3">
                        <div className="mt-2">
                            <p className="font-bold leading-[20px] tracking-[0.02em]">
                                {t("book_location.description")}
                            </p>
                            <p className="text-itemListBorder">
                                {getEquipmentName(equipment.description, langNames, "description")}
                            </p>
                        </div>
                        {useCase !== "keys" && (
                            <>
                                <div className="my-5 h-[1px] w-full bg-primary" />
                                <div className="flex flex-col items-center justify-between w-full">
                                    <div className="flex items-center justify-between w-full">
                                        <div className="font-bold leading-[20px] tracking-[0.02em]">
                                            {t("book_location.prices")}
                                        </div>
                                        <div>{taxStatement.short}</div>
                                    </div>
                                    {priceIncludeInterval && (
                                        <div className="flex items-center justify-between w-full">
                                            <div className="font-semibold leading-[20px] tracking-[0.02em]">
                                                {t("general.duration")}
                                            </div>
                                            <div className="font-semibold">
                                                {t("book_location.from")}
                                            </div>
                                        </div>
                                    )}
                                    {sortedDurations.map((durationInMinutes) => {
                                        const prices = sortedPriceScales[durationInMinutes] || [];
                                        return (
                                            <div
                                                className="flex items-center justify-between w-full text-itemListBorder"
                                                key={`${equipment.id}-${durationInMinutes}`}
                                            >
                                                <div>{convertMinutesToHours(durationInMinutes, true)}</div>
                                                <div>
                                                    {prices.length > 0
                                                        ? formatCurrency(
                                                              prices[0],
                                                              i18n.language,
                                                              tenantConfig.currency
                                                          )
                                                        : 0}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </AccordionContent>
                </AccordionItem>
            </Accordion>
        </div>
    );
}

export default memo(EquipmentListAccordion);
